import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { 
  Truck, Package, Calendar, User, Search, RefreshCw, 
  Eye, MapPin, Phone, Mail, Clock, Hash, Building2, Edit, Loader2, Save, X, Trash2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "../App";

const API = import.meta.env.VITE_API_URL || (window.location.origin + "/api");

export default function SampleDispatchesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role?.toLowerCase() === "admin";
  const [dispatches, setDispatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  
  // Edit state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingDispatch, setEditingDispatch] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Delete state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingDispatch, setDeletingDispatch] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchDispatches = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/sample-dispatches`);
      if (response.data.success) {
        setDispatches(response.data.dispatches || []);
      }
    } catch (error) {
      console.error("Error fetching dispatches:", error);
      toast.error("Failed to load dispatch records");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDispatches();
  }, []);

  // Open edit dialog
  const openEditDialog = (dispatch) => {
    setEditingDispatch({
      ...dispatch,
      dispatchDetails: dispatch.dispatchDetails || [{ modeOfDispatch: "", dispatchType: "", courierName: "", dispatchDate: "", expectedDeliveryDate: "" }],
      handoverInfo: dispatch.handoverInfo || [{ dispatchedBy: "", handoverTime: "", fileUpload: "" }]
    });
    setShowEditDialog(true);
  };

  // Handle edit field change
  const handleEditChange = (field, value) => {
    setEditingDispatch(prev => ({ ...prev, [field]: value }));
  };

  // Handle dispatch details subform change
  const handleDispatchDetailChange = (index, field, value) => {
    setEditingDispatch(prev => {
      const updated = [...(prev.dispatchDetails || [])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, dispatchDetails: updated };
    });
  };

  // Handle handover info subform change
  const handleHandoverInfoChange = (index, field, value) => {
    setEditingDispatch(prev => {
      const updated = [...(prev.handoverInfo || [])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, handoverInfo: updated };
    });
  };

  // Save edited dispatch
  const handleSaveEdit = async () => {
    if (!editingDispatch) return;
    
    setSaving(true);
    try {
      const response = await axios.put(`${API}/sample-dispatch/${editingDispatch.id}`, editingDispatch);
      
      if (response.data.success) {
        toast.success("Dispatch record updated successfully!");
        if (response.data.zohoSync?.success) {
          toast.success("✅ Synced to Zoho Creator", {
            description: `Record ID: ${response.data.zohoSync.recordId || "Updated"}`
          });
        } else if (response.data.zohoSync?.queuedForSync) {
          toast.info("📋 Queued for Zoho sync");
        }
        setShowEditDialog(false);
        setEditingDispatch(null);
        fetchDispatches();
      } else {
        toast.error(response.data.error || "Failed to update dispatch");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update dispatch");
    } finally {
      setSaving(false);
    }
  };

  // Delete dispatch (Admin only)
  const handleDelete = async () => {
    if (!deletingDispatch) return;
    
    setDeleting(true);
    try {
      const response = await axios.delete(`${API}/sample-dispatch/${deletingDispatch.id}`, {
        params: { user_role: user?.role }
      });
      
      if (response.data.success) {
        toast.success("Dispatch record deleted successfully!");
        setShowDeleteDialog(false);
        setDeletingDispatch(null);
        fetchDispatches();
      } else {
        toast.error(response.data.error || "Failed to delete dispatch");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete dispatch");
    } finally {
      setDeleting(false);
    }
  };

  // Filter dispatches
  const filteredDispatches = dispatches.filter(d => {
    const matchesSearch = 
      (d.sampleNo?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
      (d.productName?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
      (d.clientName?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
      (d.trackingNumber?.toLowerCase() || "").includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === "all" || d.dispatchStatus === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // Status badge styling
  const getStatusBadge = (status) => {
    const styles = {
      "Pending": "bg-amber-100 text-amber-700 border-amber-200",
      "Dispatched": "bg-blue-100 text-blue-700 border-blue-200",
      "In Transit": "bg-purple-100 text-purple-700 border-purple-200",
      "Delivered": "bg-green-100 text-green-700 border-green-200",
      "Cancelled": "bg-red-100 text-red-700 border-red-200"
    };
    return styles[status] || "bg-slate-100 text-slate-600 border-slate-200";
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-IN', { 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('en-IN', { 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  // Get dispatch date from first dispatch detail
  const getDispatchDate = (dispatch) => {
    if (dispatch.dispatchDetails && dispatch.dispatchDetails.length > 0) {
      return formatDate(dispatch.dispatchDetails[0].dispatchDate);
    }
    return "-";
  };

  // Get courier name from first dispatch detail
  const getCourierName = (dispatch) => {
    if (dispatch.dispatchDetails && dispatch.dispatchDetails.length > 0) {
      return dispatch.dispatchDetails[0].courierName || "-";
    }
    return "-";
  };

  return (
    <div className="p-6 md:p-8" data-testid="sample-dispatches-page">
      {/* Header */}
      <motion.div 
        className="mb-8"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold font-poppins bg-gradient-to-r from-vcc-teal to-vcc-pink bg-clip-text text-transparent">
              Sample Dispatches
            </h1>
            <p className="text-slate-500 font-poppins mt-1">
              Track and manage all sample dispatch records
            </p>
          </div>
          <Button
            onClick={fetchDispatches}
            variant="outline"
            className="font-poppins"
            data-testid="refresh-dispatches-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </motion.div>

      {/* Stats Cards */}
      <motion.div 
        className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Truck className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold font-poppins text-slate-800">{dispatches.length}</p>
                <p className="text-xs text-slate-500 font-poppins">Total Dispatches</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold font-poppins text-slate-800">
                  {dispatches.filter(d => d.dispatchStatus === "Pending").length}
                </p>
                <p className="text-xs text-slate-500 font-poppins">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Package className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold font-poppins text-slate-800">
                  {dispatches.filter(d => d.dispatchStatus === "In Transit").length}
                </p>
                <p className="text-xs text-slate-500 font-poppins">In Transit</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Truck className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold font-poppins text-slate-800">
                  {dispatches.filter(d => d.dispatchStatus === "Delivered").length}
                </p>
                <p className="text-xs text-slate-500 font-poppins">Delivered</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Filters */}
      <motion.div 
        className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm mb-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by sample no, product, client, or tracking..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 font-poppins"
              data-testid="search-dispatches"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-48 font-poppins">
              <SelectValue placeholder="Filter Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="Pending">Pending</SelectItem>
              <SelectItem value="Dispatched">Dispatched</SelectItem>
              <SelectItem value="In Transit">In Transit</SelectItem>
              <SelectItem value="Delivered">Delivered</SelectItem>
              <SelectItem value="Cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </motion.div>

      {/* Dispatches Table */}
      <motion.div 
        className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        {loading ? (
          <div className="p-12 text-center">
            <RefreshCw className="w-8 h-8 mx-auto text-vcc-teal animate-spin mb-4" />
            <p className="text-slate-500 font-poppins">Loading dispatch records...</p>
          </div>
        ) : filteredDispatches.length === 0 ? (
          <div className="p-12 text-center">
            <Truck className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500 font-poppins mb-2">No dispatch records found</p>
            <p className="text-sm text-slate-400 font-poppins">
              {searchTerm || filterStatus !== "all"
                ? "Try adjusting your filters"
                : "Dispatch a sample from Product Development to get started"}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-poppins font-semibold">Sample No</TableHead>
                  <TableHead className="font-poppins font-semibold">Product Name</TableHead>
                  <TableHead className="font-poppins font-semibold">Client Name</TableHead>
                  <TableHead className="font-poppins font-semibold">Dispatch Date</TableHead>
                  <TableHead className="font-poppins font-semibold">Courier</TableHead>
                  <TableHead className="font-poppins font-semibold">Tracking No</TableHead>
                  <TableHead className="font-poppins font-semibold">Status</TableHead>
                  <TableHead className="font-poppins font-semibold">Created</TableHead>
                  <TableHead className="font-poppins font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDispatches.map((dispatch, index) => (
                  <TableRow key={dispatch.id || index} className="hover:bg-slate-50">
                    <TableCell className="font-poppins font-medium text-vcc-teal">
                      {dispatch.sampleNo || "-"}
                    </TableCell>
                    <TableCell>
                      <span className="font-poppins text-slate-700">
                        {dispatch.productName || "-"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-poppins font-medium text-slate-800">
                          {dispatch.clientName || "-"}
                        </p>
                        {dispatch.clientEmail && (
                          <p className="text-xs text-slate-500 font-poppins flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {dispatch.clientEmail}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1 text-slate-600 font-poppins text-sm">
                        <Calendar className="w-3 h-3" />
                        {getDispatchDate(dispatch)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="font-poppins text-slate-600 text-sm">
                        {getCourierName(dispatch)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="font-poppins text-slate-700 font-medium text-sm">
                        {dispatch.trackingNumber || "-"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${getStatusBadge(dispatch.dispatchStatus)} font-poppins border`}>
                        {dispatch.dispatchStatus || "Pending"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-xs text-slate-500 font-poppins">
                          {formatDateTime(dispatch.createdAt)}
                        </p>
                        {dispatch.createdBy && (
                          <p className="text-xs text-slate-400 font-poppins flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {dispatch.createdBy}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          onClick={() => openEditDialog(dispatch)}
                          style={{ backgroundColor: '#2563eb', color: 'white' }}
                          className="font-poppins hover:opacity-90 shadow-md"
                          data-testid={`edit-dispatch-${dispatch.sampleNo}`}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/products/${encodeURIComponent(dispatch.sampleNo)}`)}
                          className="font-poppins"
                          data-testid={`view-dispatch-${dispatch.sampleNo}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                        {isAdmin && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setDeletingDispatch(dispatch);
                              setShowDeleteDialog(true);
                            }}
                            className="font-poppins text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-dispatch-${dispatch.sampleNo}`}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </motion.div>

      {/* Footer Stats */}
      {!loading && filteredDispatches.length > 0 && (
        <motion.div 
          className="mt-4 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <p className="text-sm text-slate-500 font-poppins">
            Showing {filteredDispatches.length} of {dispatches.length} dispatch records
          </p>
        </motion.div>
      )}

      {/* Edit Dispatch Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-poppins text-xl flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-600" />
              Edit Dispatch - {editingDispatch?.sampleNo}
            </DialogTitle>
          </DialogHeader>

          {editingDispatch && (
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Sample No</Label>
                  <Input value={editingDispatch.sampleNo || ""} disabled className="mt-1 bg-slate-50" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Product Name</Label>
                  <Input 
                    value={editingDispatch.productName || ""} 
                    onChange={(e) => handleEditChange("productName", e.target.value)}
                    className="mt-1" 
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Client Name</Label>
                  <Input 
                    value={editingDispatch.clientName || ""} 
                    onChange={(e) => handleEditChange("clientName", e.target.value)}
                    className="mt-1" 
                  />
                </div>
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Client Email</Label>
                  <Input 
                    value={editingDispatch.clientEmail || ""} 
                    onChange={(e) => handleEditChange("clientEmail", e.target.value)}
                    className="mt-1" 
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Contact Number</Label>
                  <Input 
                    value={editingDispatch.contactNumber || ""} 
                    onChange={(e) => handleEditChange("contactNumber", e.target.value)}
                    className="mt-1" 
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Location</Label>
                  <Input 
                    value={editingDispatch.location || ""} 
                    onChange={(e) => handleEditChange("location", e.target.value)}
                    className="mt-1" 
                  />
                </div>
              </div>

              {/* Product Info */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Product Type</Label>
                  <Input 
                    value={editingDispatch.productType || ""} 
                    onChange={(e) => handleEditChange("productType", e.target.value)}
                    className="mt-1" 
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Packing Type</Label>
                  <Input 
                    value={editingDispatch.packingType || ""} 
                    onChange={(e) => handleEditChange("packingType", e.target.value)}
                    className="mt-1" 
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">SKU</Label>
                  <Input 
                    value={editingDispatch.sku || ""} 
                    onChange={(e) => handleEditChange("sku", e.target.value)}
                    className="mt-1" 
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Quantity</Label>
                  <Input 
                    value={editingDispatch.quantity || ""} 
                    onChange={(e) => handleEditChange("quantity", e.target.value)}
                    className="mt-1" 
                  />
                </div>
              </div>

              {/* Dispatch Details */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Tracking Number</Label>
                  <Input 
                    value={editingDispatch.trackingNumber || ""} 
                    onChange={(e) => handleEditChange("trackingNumber", e.target.value)}
                    className="mt-1" 
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Dispatch Status</Label>
                  <Select 
                    value={editingDispatch.dispatchStatus || "Pending"} 
                    onValueChange={(value) => handleEditChange("dispatchStatus", value)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Pending">Pending</SelectItem>
                      <SelectItem value="Dispatched">Dispatched</SelectItem>
                      <SelectItem value="In Transit">In Transit</SelectItem>
                      <SelectItem value="Delivered">Delivered</SelectItem>
                      <SelectItem value="Cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Priority</Label>
                  <Select 
                    value={editingDispatch.samplePriority || "Medium"} 
                    onValueChange={(value) => handleEditChange("samplePriority", value)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Low">Low</SelectItem>
                      <SelectItem value="Medium">Medium</SelectItem>
                      <SelectItem value="High">High</SelectItem>
                      <SelectItem value="Urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Dispatch Details Subform */}
              <div>
                <Label className="text-sm text-slate-700 font-poppins font-medium">Dispatch Details</Label>
                <div className="mt-2 space-y-2">
                  {editingDispatch.dispatchDetails?.map((detail, index) => (
                    <div key={index} className="grid grid-cols-5 gap-2 p-2 bg-slate-50 rounded">
                      <Input 
                        placeholder="Mode of Dispatch"
                        value={detail.modeOfDispatch || ""}
                        onChange={(e) => handleDispatchDetailChange(index, "modeOfDispatch", e.target.value)}
                        className="text-sm"
                      />
                      <Input 
                        placeholder="Dispatch Type"
                        value={detail.dispatchType || ""}
                        onChange={(e) => handleDispatchDetailChange(index, "dispatchType", e.target.value)}
                        className="text-sm"
                      />
                      <Input 
                        placeholder="Courier Name"
                        value={detail.courierName || ""}
                        onChange={(e) => handleDispatchDetailChange(index, "courierName", e.target.value)}
                        className="text-sm"
                      />
                      <Input 
                        type="date"
                        placeholder="Dispatch Date"
                        value={detail.dispatchDate || ""}
                        onChange={(e) => handleDispatchDetailChange(index, "dispatchDate", e.target.value)}
                        className="text-sm"
                      />
                      <Input 
                        type="date"
                        placeholder="Expected Delivery"
                        value={detail.expectedDeliveryDate || ""}
                        onChange={(e) => handleDispatchDetailChange(index, "expectedDeliveryDate", e.target.value)}
                        className="text-sm"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Handover Info Subform */}
              <div>
                <Label className="text-sm text-slate-700 font-poppins font-medium">Handover Information</Label>
                <div className="mt-2 space-y-2">
                  {editingDispatch.handoverInfo?.map((info, index) => (
                    <div key={index} className="grid grid-cols-2 gap-2 p-2 bg-slate-50 rounded">
                      <Input 
                        placeholder="Dispatched By"
                        value={info.dispatchedBy || ""}
                        onChange={(e) => handleHandoverInfoChange(index, "dispatchedBy", e.target.value)}
                        className="text-sm"
                      />
                      <Input 
                        type="datetime-local"
                        placeholder="Handover Time"
                        value={info.handoverTime || ""}
                        onChange={(e) => handleHandoverInfoChange(index, "handoverTime", e.target.value)}
                        className="text-sm"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Remarks */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Remarks / Notes</Label>
                  <Textarea 
                    value={editingDispatch.remarks || ""} 
                    onChange={(e) => handleEditChange("remarks", e.target.value)}
                    className="mt-1" 
                    rows={3}
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Additional Requirements</Label>
                  <Textarea 
                    value={editingDispatch.additionalRequirements || ""} 
                    onChange={(e) => handleEditChange("additionalRequirements", e.target.value)}
                    className="mt-1" 
                    rows={3}
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="flex gap-2 mt-4">
            <Button 
              variant="outline" 
              onClick={() => setShowEditDialog(false)}
              className="font-poppins"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={saving}
              style={{ backgroundColor: '#2563eb', color: 'white' }}
              className="hover:opacity-90 font-poppins shadow-md"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save & Sync to Zoho
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog (Admin Only) */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-poppins text-red-600">Delete Dispatch Record</AlertDialogTitle>
            <AlertDialogDescription className="font-poppins">
              Are you sure you want to delete the dispatch record for <strong>{deletingDispatch?.sampleNo}</strong>?
              <br /><br />
              This will also remove the record from Zoho Creator. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="font-poppins" disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 font-poppins"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
